import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {useToggle, useWasUnmounted} from '../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {AsyncStatusesEnum} from '../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {CompanyModel} from '../../../libs/shared/core/src/models/api/company/companyModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../libs/shared/core/src/services/tracking/tracking';
import {getIsUserGlobalAdmin} from '../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {OkCancelDialog} from '../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {DynamicScrollBoundaries} from '../../../libs/shared/ui/src/components/scroll/dynamicScrollBoundaries';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../helpers/appReduxHelpers';
import {HelpCenterSearchCache} from '../../models/api/localPreferencesModel';
import {SearchHelpCenterResultModel} from '../../models/api/searchHelpCenterModel';
import {LmsCardLocationsEnum} from '../../settings/lms/lmsHelpers';
import {useInfoBubblesContext} from '../common/infoBubbles/infoBubblesContext';
import {useSidePanelContext} from '../inboxes/side/sidePanelContext';
import {SidePanelHeader} from '../inboxes/side/sidePanelHeader';
import {LmsCard, LmsCardOrientationsEnum} from '../lms/LmsCard';
import {HelpCenterSearch} from './helpCenterSearch/helpCenterSearch';
import {HelpCenterSearchResults} from './helpCenterSearch/helpCenterSearchResults';
import {StartGuideAddEmailModal} from './startGuideAddEmailModal';
import {StartGuideArticle} from './startGuideArticle';
import {startGuideAdminArticles, startGuideUserArticles} from './startGuideArticlesCommon';
import {StartGuideTaskDefinition, StartGuideTaskInteractionsEnum} from './startGuideCommon';
import {StartGuideFooterLinks} from './startGuideFooterLinks';
import {StartGuideTask} from './startGuideTask';
import {StartGuideTaskSuccess} from './startGuideTaskSuccess';

/*
 * Constants.
 */

const getStartedText = <Trans id="admin-onboarding-guide-title">Get started</Trans>;

/*
 * Props.
 */

export interface StartGuideProps {
  givenName: string;
  tasks: ReadonlyArray<StartGuideTaskDefinition>;
  isUserFirstGlobalAdmin: boolean;
  isGlobalAdmin: boolean;
  company: CompanyModel;
  companyName: string | null;
  userNamespace: string;
  hasEmailComposeChannel: boolean;
  shouldAddTeamInbox: boolean;
  shouldShowStartGuideTasks: boolean;
  shouldShowSuccessScreen: boolean;
  shouldShowOpenTeamInbox: boolean;
  composeTeamInboxAccessRequest: () => void;
  openEmailComposer: () => void;
  navigateToTeamInbox: () => void;
  navigateToAdminHome: () => void;
  onDismissSuccessScreen: () => void;
  onOpenHelpChatClick: () => void;
  onOpenBlogClick: () => void;
  navigate: (destination: string) => () => void;
  initialSearchCache?: HelpCenterSearchCache;
  onSaveSearchQuery: (query?: string, result?: SearchHelpCenterResultModel) => void;
  numCompletedTasks: number;
  numTotalTasks: number;
}

/*
 * Style.
 */

const StyledStartInvitedTeammateGuide = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-template-rows: 92px 1fr 68px;
  height: 100%;
  width: 100%;
  position: absolute;
`;

const StyledContentDiv = styled.div`
  padding: 16px;
  overflow: scroll;
`;

const StyledHeaderDiv = styled.div`
  font-size: ${fontSizes.large};
  line-height: 25px;
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.bold};
`;

const StyledTasksWrapperDiv = styled.div`
  margin: 10px 0px;
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: auto;
  grid-row-gap: 12px;
`;

const StyledSectionSpacer = styled.div`
  min-height: 30px;
`;

const StyledSidePanelHeader = styled(SidePanelHeader)`
  display: block;
  border-bottom: 0px;
`;

/*
 * Component.
 */

export const StartGuide: FC<StartGuideProps> = (props) => {
  const theme = useUserTheme();
  /** Help center search state. */
  const {initialSearchCache, shouldShowStartGuideTasks, onSaveSearchQuery} = props;
  const [helpCenterSearchQuery, setHelpCenterSearchQuery] = useState(initialSearchCache?.query);
  const [helpCenterSearchStatus, setHelpCenterSearchStatus] = useState<AsyncStatusesEnum>();
  const [helpCenterSearchResults, setHelpCenterSearchResults] = useState(initialSearchCache?.result);

  const isUserGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);

  // Update search query on unmount
  const {wasUnmountedRef} = useWasUnmounted();
  useEffect(
    () => () => {
      if (wasUnmountedRef.current) {
        onSaveSearchQuery(helpCenterSearchQuery, helpCenterSearchResults);
      }
    },
    [wasUnmountedRef, onSaveSearchQuery, helpCenterSearchQuery, helpCenterSearchResults],
  );

  useInitialEffect(() => {
    trackInterfaceEvent(InterfaceEventsEnum.HELP_CENTER_VIEW, {
      user_track: props.isGlobalAdmin ? 'admin' : 'non_admin',
    });
  });

  const onHelpCenterSearchResultsChange = useCallback(
    (status: AsyncStatusesEnum, result?: SearchHelpCenterResultModel) => {
      setHelpCenterSearchStatus(status);
      setHelpCenterSearchResults(result);
    },
    [],
  );

  const StyledStartGuideWrapper = StyledStartInvitedTeammateGuide;

  const shouldShowP4StartGuideTasks = !isUserGlobalAdmin && shouldShowStartGuideTasks;

  const {closeSidePanel} = useSidePanelContext();
  const onCloseSidePanelAndMaybeDismiss = () => {
    if (props.shouldShowSuccessScreen) {
      props.onDismissSuccessScreen();
    }

    closeSidePanel();
  };

  return (
    <StyledStartGuideWrapper>
      <StyledSidePanelHeader
        title={<Trans id="sidepanel-startguide-header-titlehelpcenter">Help and tips</Trans>}
        onClose={onCloseSidePanelAndMaybeDismiss}
      >
        <HelpCenterSearch
          initialQuery={initialSearchCache?.query}
          onQueryChange={setHelpCenterSearchQuery}
          onResultsChange={onHelpCenterSearchResultsChange}
          company={props.company}
        />
      </StyledSidePanelHeader>
      {!helpCenterSearchQuery && (
        <DynamicScrollBoundaries hasTopBoundary hasBottomBoundary color={theme.greys.shade30}>
          <StyledContentDiv>
            {!shouldShowP4StartGuideTasks && !props.shouldShowSuccessScreen && (
              <LmsCard orientation={LmsCardOrientationsEnum.VERTICAL} location={LmsCardLocationsEnum.P4} />
            )}
            {shouldShowP4StartGuideTasks && !props.shouldShowSuccessScreen && (
              <StartGuideContent {...props} />
            )}
            {props.shouldShowSuccessScreen && (
              <StartGuideTaskSuccess
                onDismiss={onCloseSidePanelAndMaybeDismiss}
                isAdmin={props.isGlobalAdmin}
              />
            )}
            {maybeRenderUserArticles(!props.isGlobalAdmin)}
            {maybeRenderAdminArticles(props.isGlobalAdmin)}
          </StyledContentDiv>
        </DynamicScrollBoundaries>
      )}
      {helpCenterSearchQuery && (
        <DynamicScrollBoundaries hasTopBoundary hasBottomBoundary color={theme.greys.shade30}>
          <StyledContentDiv>
            <HelpCenterSearchResults
              query={helpCenterSearchQuery}
              status={helpCenterSearchStatus}
              results={helpCenterSearchResults}
            />
          </StyledContentDiv>
        </DynamicScrollBoundaries>
      )}
      <StartGuideFooterLinks
        onOpenHelpChatClick={props.onOpenHelpChatClick}
        onOpenBlogClick={props.onOpenBlogClick}
      />
    </StyledStartGuideWrapper>
  );
};

const StartGuideContent: FC<StartGuideProps> = (props) => {
  const [isAddEmailDialogOpen, toggleIsAddEmailDialogOpen] = useToggle(false);
  const [isSendAddEmailDialogOpen, toggleIsSendAddEmailDialogOpen] = useToggle(false);
  const [isRequestAccessDialogOpen, toggleIsRequestAccessDialogOpen] = useToggle(false);
  const tasksRef = useRef<HTMLDivElement>(null);

  // Get infoBubblesContext to maybe open infoBubbles.
  const infoBubblesContext = useInfoBubblesContext();
  const {openInfoBubble} = infoBubblesContext;

  const renderTasks = () => {
    if (props.tasks.length < 1) {
      return null;
    }

    return props.tasks.map((t, order) => {
      let onClick: () => void;
      if (
        t.interactionId === StartGuideTaskInteractionsEnum.MENTION_TEAMMATE &&
        toggleIsRequestAccessDialogOpen
      ) {
        onClick = () => {
          if (!t.isDisabled && t.infoBubble) {
            openInfoBubble(t.infoBubble);
            return;
          }

          toggleIsRequestAccessDialogOpen();
        };
      } else if (t.infoBubble) {
        onClick = () => t.infoBubble && openInfoBubble(t.infoBubble);
      } else if (
        t.interactionId === StartGuideTaskInteractionsEnum.ADD_WORK_EMAIL &&
        toggleIsAddEmailDialogOpen
      ) {
        onClick = () => {
          toggleIsAddEmailDialogOpen();
          t.onClick();
        };
      } else if (
        t.interactionId === StartGuideTaskInteractionsEnum.SEND_FIRST_EMAIL &&
        toggleIsSendAddEmailDialogOpen
      ) {
        onClick = () => {
          if (!props.hasEmailComposeChannel) {
            toggleIsSendAddEmailDialogOpen();
          }
          t.onClick();
        };
      } else {
        onClick = t.onClick;
      }

      const trackOnClick = () => {
        trackInterfaceEvent(InterfaceEventsEnum.HELP_CENTER_TASK_CLICK, {
          category: 'task',
          user_track: props.isGlobalAdmin ? 'admin' : 'non_admin',
          item: t.interactionId,
        });
        onClick();
      };
      return <StartGuideTask key={t.iconName} {...t} onClick={trackOnClick} />;
    });
  };

  return (
    <>
      <div ref={tasksRef}>
        <StyledHeaderDiv>{getStartedText}</StyledHeaderDiv>
        <StyledTasksWrapperDiv>{renderTasks()}</StyledTasksWrapperDiv>
      </div>
      {maybeRenderDialog(
        isAddEmailDialogOpen,
        toggleIsAddEmailDialogOpen,
        isSendAddEmailDialogOpen,
        toggleIsSendAddEmailDialogOpen,
        isRequestAccessDialogOpen,
        toggleIsRequestAccessDialogOpen,
        props.composeTeamInboxAccessRequest,
        props.openEmailComposer,
        props.navigateToTeamInbox,
        props.navigate,
        props.shouldAddTeamInbox,
        props.shouldShowOpenTeamInbox,
        props.hasEmailComposeChannel,
        props.userNamespace,
      )}
      <StyledSectionSpacer />
    </>
  );
};

/*
 * Tasks.
 */

function maybeRenderUserArticles(shouldShow: boolean) {
  if (!shouldShow) {
    return null;
  }

  return (
    <>
      <StyledHeaderDiv>
        <Trans id="startguide-article-userheader">Learn more about Front</Trans>
      </StyledHeaderDiv>
      <StyledTasksWrapperDiv>
        {startGuideUserArticles.map((article) => {
          const onClick = () => {
            trackInterfaceEvent(InterfaceEventsEnum.HELP_CENTER_TASK_CLICK, {
              category: 'article',
              user_track: 'non_admin',
              item: article.id,
            });
            article.onClick();
          };
          return <StartGuideArticle key={article.id} {...article} onClick={onClick} />;
        })}
      </StyledTasksWrapperDiv>
    </>
  );
}

function maybeRenderAdminArticles(shouldShow: boolean) {
  if (!shouldShow) {
    return null;
  }

  return (
    <>
      <StyledHeaderDiv>
        <Trans id="startguide-article-adminheader">Set up your team for success</Trans>
      </StyledHeaderDiv>
      <StyledTasksWrapperDiv>
        {startGuideAdminArticles.map((article) => {
          const onClick = () => {
            trackInterfaceEvent(InterfaceEventsEnum.HELP_CENTER_TASK_CLICK, {
              category: 'article',
              user_track: 'admin',
              item: article.id,
            });
            article.onClick();
          };
          return <StartGuideArticle key={article.id} {...article} onClick={onClick} />;
        })}
      </StyledTasksWrapperDiv>
    </>
  );
}

function maybeRenderDialog(
  isAddEmailDialogOpen: boolean,
  toggleIsAddEmailDialogOpen: () => void,
  isSendAddEmailDialogOpen: boolean,
  toggleIsSendAddEmailDialogOpen: () => void,
  isRequestAccessDialogOpen: boolean,
  toggleIsRequestAccessDialogOpen: () => void,
  composeTeamInboxAccessRequest: () => void,
  openEmailComposer: () => void,
  navigateToTeamInbox: () => void,
  navigate: (destination: string) => () => void,
  shouldAddTeamInbox: boolean,
  shouldShowOpenTeamInbox: boolean,
  hasEmailComposeChannel: boolean,
  userNamespace: string,
) {
  if (isAddEmailDialogOpen) {
    return (
      <StartGuideAddEmailModal
        navigate={navigate}
        onDismiss={toggleIsAddEmailDialogOpen}
        userNamespace={userNamespace}
      />
    );
  }

  if (isSendAddEmailDialogOpen) {
    return (
      <StartGuideAddEmailModal
        navigate={navigate}
        onDismiss={toggleIsSendAddEmailDialogOpen}
        userNamespace={userNamespace}
        isSending
      />
    );
  }

  if (isRequestAccessDialogOpen) {
    const onOkClick = () => {
      toggleIsRequestAccessDialogOpen();

      if (shouldAddTeamInbox) {
        composeTeamInboxAccessRequest();
        return;
      }

      if (shouldShowOpenTeamInbox) {
        navigateToTeamInbox();
        return;
      }

      openEmailComposer();
    };

    return renderMentionWarningDialog(
      userNamespace,
      shouldAddTeamInbox,
      shouldShowOpenTeamInbox,
      hasEmailComposeChannel,
      onOkClick,
      toggleIsRequestAccessDialogOpen,
      navigate,
    );
  }

  return null;
}

function renderMentionWarningDialog(
  userNamespace: string,
  shouldAddTeamInbox: boolean,
  shouldShowOpenTeamInbox: boolean,
  hasEmailComposeChannel: boolean,
  onOkClick: () => void,
  onRequestClose: () => void,
  navigate: (destination: string) => () => void,
) {
  if (shouldAddTeamInbox) {
    return (
      <OkCancelDialog
        title={<Trans id="startguide-dialog-requestaccesstitle">Request access to shared inboxes</Trans>}
        okButtonLabel={<Trans id="startguide-dialog-requestaccessbutton">Request access</Trans>}
        onOkClick={onOkClick}
        onRequestClose={onRequestClose}
      >
        <Trans id="startguide-dialog-requestaccessmessage">
          To leave a comment, you need access to emails in Front. Request access to shared inboxes.
        </Trans>
      </OkCancelDialog>
    );
  }

  if (shouldShowOpenTeamInbox) {
    return (
      <OkCancelDialog
        title={<Trans id="startguide-dialog-openconversationtitle">Select a conversation</Trans>}
        okButtonLabel={<Trans id="startguide-dialog-openconversationbutton">Open a conversation</Trans>}
        onOkClick={onOkClick}
        onRequestClose={onRequestClose}
      >
        <Trans id="startguide-dialog-openconversationmessage">
          To leave a comment, first open a conversation.
        </Trans>
      </OkCancelDialog>
    );
  }

  if (hasEmailComposeChannel) {
    return (
      <OkCancelDialog
        title={<Trans id="startguide-dialog-startconversationtitle">Send an email</Trans>}
        okButtonLabel={<Trans id="startguide-dialog-startconversationbutton">Send a message</Trans>}
        onOkClick={onOkClick}
        onRequestClose={onRequestClose}
      >
        <Trans id="startguide-dialog-startconversationmessage">
          To leave a comment, first send a message where you can leave a comment.
        </Trans>
      </OkCancelDialog>
    );
  }

  return (
    <StartGuideAddEmailModal
      navigate={navigate}
      onDismiss={onRequestClose}
      userNamespace={userNamespace}
      isCommenting
    />
  );
}
